// Section « Loges » : file d'envoi des fichiers déposés. Elle vit au niveau du module, pas de la vue : un envoi
// continue si l'on change de dossier, de projet ou de rubrique, et la vue la retrouve en revenant.
// Un fichier à la fois : aperçus fabriqués ici (src/core/logePreview.ts), puis start → morceaux → aperçus → finish.
// Fichier 3D (STEP, IGES, GLB) : « Conversion 3D… » fabrique la maquette de consultation et son affiche
// (src/core/logeModelBuild.ts, chargé seulement à ce moment-là) ; si elle échoue, l'original part quand même.
// Export KeyShotXR (dossier déposé, ou son ZIP) : reconnu au dépôt, ses vues deviennent les pages d'aperçu d'un rendu
// 360° (src/core/logeXrBuild.ts, chargé au premier dossier ou ZIP) ; l'original est le ZIP, fabriqué ici pour un dossier.
import { baseName, MAX_STUDIO_FILE_BYTES, modelSource, nextVersion, type LogeLang } from '../core/loge';
import { buildPreviews, coverPreview, type PreviewKind } from '../core/logePreview';
import { xrFrameCount, xrSummary, type XrMeta } from '../core/logeXr';
import type { XrPlan } from '../core/logeXrBuild';
import { newUploadProgress, uploadToLoge, type UploadProgress } from '../data/loge';
import { fmtSize } from './logeKit';
import { showToast } from './toast';

export type UploadStatus = 'waiting' | 'preview' | 'uploading' | 'done' | 'error';

export interface UploadJob {
  key: string;
  file: File;
  projectId: string;
  folderId: string;
  folderName: string;
  clientName: string;
  lang: LogeLang;
  caption: string;
  status: UploadStatus;
  sent: number;
  total: number;
  /** Version que prendra le fichier (prévue d'après le dossier, puis donnée par le serveur). */
  version: number;
  previews: Blob[] | null;
  previewKind: PreviewKind | 'xr';
  previewNote: string;
  thumbUrl: string | null;
  error: string | null;
  /** L'erreur ne passera pas en réessayant (fichier trop lourd…). */
  fatal: boolean;
  state: UploadProgress;
  abort: AbortController | null;
  /** Identifiant du fichier une fois dans La Loge. */
  fileId: string | null;
  /** Couverture du projet (dossier caché, aperçu sans filigrane). */
  cover: boolean;
  /** Maquette 3D de consultation (GLB) à envoyer avec le fichier ; vidée une fois partie. */
  model: Blob | null;
  /** Export KeyShotXR reconnu (dossier déposé, ou ZIP lu au moment de l'aperçu). */
  xrPlan: XrPlan | null;
  /** Rendu 360° prêt : la grille des vues, envoyée avec le fichier. */
  xr: XrMeta | null;
  /** Dossier déposé : l'original (un ZIP) reste à fabriquer avant l'envoi. */
  needsZip: boolean;
}

const queue: UploadJob[] = [];
const listeners = new Set<(job: UploadJob | null) => void>();
let running = false;
let seq = 0;
let finishedSinceIdle = 0;

export function onUploads(cb: (job: UploadJob | null) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function emit(job: UploadJob | null): void {
  listeners.forEach((l) => { try { l(job); } catch (e) { console.warn('La Loge, file d’envoi :', e); } });
}

export function uploadJobs(projectId?: string): UploadJob[] {
  return queue.filter((j) => !projectId || j.projectId === projectId);
}

export function activeUploads(): number {
  return queue.filter((j) => j.status === 'waiting' || j.status === 'preview' || j.status === 'uploading').length;
}

export interface EnqueueContext {
  projectId: string;
  folderId: string;
  folderName: string;
  clientName: string;
  lang: LogeLang;
  /** Fichiers déjà présents dans le dossier (corbeille comprise), pour annoncer la version. */
  existing: Array<{ name: string; version: number }>;
  /** Image de couverture du projet (`folderId` peut alors être vide : le serveur range le fichier). */
  cover?: boolean;
}

function newJob(file: File, ctx: EnqueueContext, size: number, extra: Partial<UploadJob> = {}): UploadJob {
  const known = [
    ...ctx.existing.map((f) => ({ base_name: baseName(f.name), version: f.version })),
    ...queue.filter((j) => j.folderId === ctx.folderId && j.status !== 'error').map((j) => ({ base_name: baseName(j.file.name), version: j.version })),
  ];
  const tooBig = size > MAX_STUDIO_FILE_BYTES;
  return {
    key: `up${++seq}`, file, projectId: ctx.projectId, folderId: ctx.folderId, folderName: ctx.folderName, clientName: ctx.clientName, lang: ctx.lang,
    caption: '', status: tooBig ? 'error' : 'waiting', sent: 0, total: size, version: nextVersion(known, file.name),
    previews: null, previewKind: null, previewNote: '', thumbUrl: null,
    error: tooBig ? `Fichier trop lourd : ${fmtSize(size)}, ${fmtSize(MAX_STUDIO_FILE_BYTES)} au plus.` : null, fatal: tooBig,
    state: newUploadProgress(), abort: null, fileId: null, cover: ctx.cover === true, model: null, xrPlan: null, xr: null, needsZip: false,
    ...extra,
  };
}

export function enqueueUploads(files: File[], ctx: EnqueueContext): void {
  for (const file of files) queue.push(newJob(file, ctx, file.size));
  emit(null);
  void pump();
}

/** Un export KeyShotXR déposé en dossier : un seul fichier, le ZIP de l'export, fabriqué au moment de l'envoi. */
export function enqueueXrExport(plan: XrPlan, ctx: EnqueueContext): void {
  const zip = new File([], `${plan.name}.zip`, { type: 'application/zip' });
  queue.push(newJob(zip, ctx, plan.bytes, { xrPlan: plan, needsZip: plan.source === 'folder', previewNote: `KeyShotXR · ${xrSummary(plan.meta)}` }));
  emit(null);
  void pump();
}

/** Au-delà, un dossier ordinaire déposé demande confirmation : chacun de ses fichiers part à part. */
export const FOLDER_CONFIRM_FILES = 20;

export interface DropOutcome {
  /** Nom de l'export KeyShotXR reconnu, s'il y en a un. */
  xr: string | null;
  /** Raison d'un export KeyShotXR inexploitable (rien n'est envoyé). */
  error: string | null;
  /** Autres fichiers mis dans la file. */
  files: number;
  /** Dépôt abandonné (dossier ordinaire trop fourni, refusé). */
  cancelled: boolean;
}

/**
 * Un dépôt qui contient des dossiers (glisser-déposer, ou choix d'un dossier) : un export KeyShotXR devient un rendu
 * 360° ; les autres fichiers partent un par un, comme d'habitude (les fichiers système sont ignorés).
 * `confirmMany` : demandé avant d'envoyer un à un les fichiers d'un dossier ordinaire bien fourni.
 */
export async function enqueueDropped(items: Array<{ path: string; file: File }>, ctx: EnqueueContext, confirmMany: (n: number) => boolean): Promise<DropOutcome> {
  const { planFromFiles } = await import('../core/logeXrBuild');
  const { result, rest } = await planFromFiles(items);
  if (result && !result.ok) return { xr: null, error: result.reason, files: 0, cancelled: false };
  if (!result && rest.length > FOLDER_CONFIRM_FILES && !confirmMany(rest.length)) return { xr: null, error: null, files: 0, cancelled: true };
  if (result?.ok) enqueueXrExport(result.plan, ctx);
  if (rest.length) enqueueUploads(rest.map((r) => r.file), ctx);
  return { xr: result?.ok ? result.plan.name : null, error: null, files: rest.length, cancelled: false };
}

export function retryUpload(key: string): void {
  const j = queue.find((x) => x.key === key);
  if (!j || j.status !== 'error' || j.fatal) return;
  j.status = 'waiting';
  j.error = null;
  emit(j);
  void pump();
}

/** Retire une ligne : annule l'envoi en cours s'il y en a un (le fichier n'apparaît alors jamais dans La Loge). */
export function dismissUpload(key: string): void {
  const i = queue.findIndex((x) => x.key === key);
  if (i < 0) return;
  const j = queue[i]!;
  j.abort?.abort();
  if (j.thumbUrl) URL.revokeObjectURL(j.thumbUrl);
  queue.splice(i, 1);
  emit(null);
}

/** Efface les lignes terminées d'un projet (après relecture des fichiers). */
export function clearDone(projectId: string): void {
  for (let i = queue.length - 1; i >= 0; i--) {
    const j = queue[i]!;
    if (j.projectId === projectId && j.status === 'done') { if (j.thumbUrl) URL.revokeObjectURL(j.thumbUrl); queue.splice(i, 1); }
  }
  emit(null);
}

async function pump(): Promise<void> {
  if (running) return;
  const job = queue.find((j) => j.status === 'waiting');
  if (!job) {
    if (finishedSinceIdle > 0) {
      showToast({ id: 0, kind: 'info', text: finishedSinceIdle > 1 ? `${finishedSinceIdle} fichiers déposés dans la Loge.` : 'Fichier déposé dans la Loge.' });
      finishedSinceIdle = 0;
    }
    return;
  }
  running = true;
  try { await run(job); }
  finally {
    running = false;
    void pump();
  }
}

/**
 * Maquette 3D et affiche d'un fichier STEP, IGES ou GLB. Elle ne fait jamais échouer l'envoi : en cas d'échec,
 * l'original part sans elle, avec la raison dans la file (« Maquette 3D impossible : … »).
 */
async function buildModelFor(job: UploadJob, signal: AbortSignal): Promise<void> {
  const started = performance.now();
  let stage = 'Conversion 3D';
  const paint = () => {
    const s = Math.round((performance.now() - started) / 1000);
    job.previewNote = `${stage}… ${s} s`;
    emit(job);
  };
  paint();
  const tick = setInterval(paint, 1000);
  try {
    const { buildModel } = await import('../core/logeModelBuild');
    const r = await buildModel(job.file, job.lang, { signal, onStage: (text) => { stage = text; paint(); } });
    job.model = r.glb;
    job.previews = r.poster ? [r.poster] : [];
    job.previewKind = r.poster ? 'image' : null;
    const secs = Math.max(1, Math.round(r.ms / 1000));
    job.previewNote = `Maquette 3D : ${r.summary}${r.lighter ? ', allégée' : ''}${r.converted ? `, convertie en ${secs} s` : ''}`
      + (r.posterError ? ` · sans affiche (${r.posterError})` : '');
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    job.model = null;
    job.previews = [];
    job.previewKind = null;
    job.previewNote = `Maquette 3D impossible : ${e instanceof Error ? e.message : String(e)}`;
  } finally {
    clearInterval(tick);
  }
}

const isZip = (f: File): boolean => /\.zip$/i.test(f.name) || /^application\/(x-)?zip(-compressed)?$/i.test(f.type);

/**
 * Rendu 360° : les vues de l'export, réduites, filigranées, en JPEG (une page d'aperçu par vue). Un ZIP déposé est
 * d'abord lu : s'il ne contient pas d'export KeyShotXR, il part comme avant, sans aperçu (renvoie false).
 */
async function buildXrFor(job: UploadJob, signal: AbortSignal): Promise<boolean> {
  const x = await import('../core/logeXrBuild');
  if (!job.xrPlan) {
    job.previewNote = 'Lecture du ZIP…';
    emit(job);
    const found = await x.planFromZip(job.file);
    if (!found) return false;
    if (!found.ok) {
      job.previews = [];
      job.previewKind = null;
      job.previewNote = `${found.reason} Envoyé sans aperçu.`;
      return true;
    }
    job.xrPlan = found.plan;
  }
  const plan = job.xrPlan;
  const total = xrFrameCount(plan.meta);
  job.previewNote = `KeyShotXR · préparation de ${total} vues…`;
  emit(job);
  job.previews = await x.buildXrPreviews(plan, job.lang, {
    signal,
    onFrame: (done, n) => { job.previewNote = `KeyShotXR · vue ${done} sur ${n}…`; emit(job); },
  });
  job.previewKind = 'xr';
  job.xr = plan.meta;
  job.previewNote = `KeyShotXR · ${xrSummary(plan.meta)}`;
  return true;
}

/** Original d'un export déposé en dossier : le ZIP de ses fichiers, sans recompression. */
async function zipFor(job: UploadJob, signal: AbortSignal): Promise<void> {
  const plan = job.xrPlan;
  if (!plan) return;
  const note = job.previewNote;
  job.status = 'preview';
  job.previewNote = 'Préparation du ZIP de l’export…';
  emit(job);
  const { zipFolder } = await import('../core/logeXrBuild');
  job.file = await zipFolder(plan, { signal, onFile: (done, n) => { if (done % 10 === 0 || done === n) { job.previewNote = `Préparation du ZIP de l’export… ${done} / ${n}`; emit(job); } } });
  job.needsZip = false;
  job.previewNote = note;
}

async function run(job: UploadJob): Promise<void> {
  const ctrl = new AbortController();
  job.abort = ctrl;
  try {
    if (!job.previews) {
      job.status = 'preview';
      job.previewNote = 'Préparation de l’aperçu…';
      emit(job);
      try {
        if (!job.cover && modelSource(job.file.type, job.file.name)) {
          await buildModelFor(job, ctrl.signal);
        } else if (job.cover) {
          job.previews = [await coverPreview(job.file, ctrl.signal)];
          job.previewKind = 'image';
          job.previewNote = 'Couverture du projet';
        } else if ((job.xrPlan || isZip(job.file)) && await buildXrFor(job, ctrl.signal)) {
          // Rendu 360° (ou ZIP d'un export inexploitable, envoyé sans aperçu) : aperçus et note déjà posés.
        } else {
          const r = await buildPreviews(job.file, job.lang, {
            signal: ctrl.signal,
            onPage: (done, total) => { if (total > 1) { job.previewNote = `Aperçu, page ${done} sur ${total}…`; emit(job); } },
          });
          job.previews = r.pages;
          job.previewKind = r.kind;
          job.previewNote = r.kind === 'pdf'
            ? (r.totalPages > r.pages.length ? `${r.pages.length} premières pages en aperçu, sur ${r.totalPages}` : `${r.pages.length} page${r.pages.length > 1 ? 's' : ''} en aperçu`)
            : r.kind === 'image' ? 'Aperçu filigrané' : 'Sans aperçu : le client verra le nom et le format';
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') throw e;
        // Une couverture sans image n'a pas de sens : on s'arrête, sans rien envoyer.
        if (job.cover) { job.fatal = true; throw e; }
        // L'original part quand même ; le client ne verra que son nom et son format.
        job.previews = [];
        job.previewKind = null;
        job.xr = null;
        job.previewNote = `Aperçu impossible (${e instanceof Error ? e.message : String(e)}) : envoyé sans aperçu`;
      }
      job.previews ??= [];
      if (job.previews[0]) job.thumbUrl = URL.createObjectURL(job.previews[0]);
    }
    if (job.needsZip) await zipFor(job, ctrl.signal);
    job.total = job.file.size + job.previews.reduce((s, b) => s + b.size, 0) + (job.model?.size ?? 0);
    job.status = 'uploading';
    emit(job);
    let last = 0;
    await uploadToLoge(
      {
        folderId: job.folderId, file: job.file, previews: job.previews, model: job.model, ...(job.caption ? { caption: job.caption } : {}),
        ...(job.cover ? { cover: { projectId: job.projectId } } : {}), ...(job.xr ? { xr: job.xr } : {}),
      },
      job.state,
      (sent, total) => {
        job.sent = sent;
        job.total = total;
        if (job.state.version) job.version = job.state.version;
        const now = performance.now();
        if (now - last > 120 || sent >= total) { last = now; emit(job); }
      },
      ctrl.signal,
    );
    job.fileId = job.state.fileId;
    job.status = 'done';
    job.previews = [];
    job.model = null;
    if (job.state.modelError) job.previewNote = `Maquette 3D non envoyée : ${job.state.modelError}`;
    finishedSinceIdle++;
    emit(job);
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return;
    job.status = 'error';
    job.error = e instanceof Error ? e.message : String(e);
    emit(job);
  } finally {
    job.abort = null;
  }
}

// Fermer l'onglet pendant un envoi le perdrait : le navigateur demande confirmation.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (activeUploads() > 0) { e.preventDefault(); e.returnValue = ''; }
  });
}
